import type {
  GoldConfig,
  MaterializedMenuProduct,
} from '@iheartjane/data-access';
import type { SmartSortProduct } from '@iheartjane/dm-sdk';
import type { FeatureFlag } from '@iheartjane/feature-flags';
import type { SearchSuggestionProduct } from '@iheartjane/menu';
import { shouldShowGold as showGold } from '@iheartjane/menu';
import type { StoreType } from '@iheartjane/types';

import { trackProductListingTap } from '@jane/bloom/services/tracking/track';
import type { CustomLanguage } from '@jane/bloom/types/schemas/bloom/customLanguage';
import type { ShoppingPreferences } from '@jane/bloom/types/schemas/bloom/shoppingPreferences';
import type { BloomMenuStore } from '@jane/bloom/types/schemas/roots/bloom/menu';
import { getJaneGoldTitle } from '@jane/bloom/utils/gold/getJaneGoldTitle';
import { buildAutocompleteProduct } from '@jane/bloom/viewModels/autocompleteProduct';

export const toSuggestionProduct = ({
  customLanguage,
  featureFlags,
  generatePath,
  goldConfig,
  hideDropdown,
  pinnedStore,
  product,
  shoppingPreferences,
  storeType,
}: {
  customLanguage: CustomLanguage;
  featureFlags: Partial<Record<FeatureFlag, boolean>>;
  generatePath: (path: string) => string;
  goldConfig: GoldConfig | null;
  hideDropdown: () => void;
  pinnedStore: BloomMenuStore;
  product: SmartSortProduct<MaterializedMenuProduct>;
  shoppingPreferences: ShoppingPreferences | undefined;
  storeType: StoreType;
}): SearchSuggestionProduct => {
  const ap = buildAutocompleteProduct({
    customLanguage,
    product: product.attributes,
    specials: [],
  });

  const showGoldBadge = Boolean(
    pinnedStore &&
    !!getJaneGoldTitle({ menuProduct: product.attributes }) &&
    showGold({
      appMode: 'bloom',
      goldConfig,
      state: pinnedStore.state,
      storeId: pinnedStore.id,
    })
  );

  return {
    baseImage: ap.baseImage,
    brand: ap.brand || null,
    imageSrcSet: ap.imageSrcSet,
    kind: ap.kind,
    lineage: ap.lineage,
    name: ap.name,
    onClick: () => {
      hideDropdown();
      product.click();
      trackProductListingTap(
        {
          product: ap,
          reservationMode: shoppingPreferences?.reservationMode,
          storeId: pinnedStore.id,
          storeType,
        },
        featureFlags
      );
    },
    onImpress: () => product.impress(),
    productId: ap.product_id,
    showGoldBadge,
    specialLabel: ap.specialLabel,
    to: generatePath(
      `products/${ap.product_id}/${ap.unique_slug ?? ap.product_id}`
    ),
  };
};
